import * as yup from 'yup';
import { isPhoneNumberValid } from '@utils/phoneNumberValidation/phoneNumberValidation';
import type { PasswordRequirements } from '@t/checkout.types';
import { getDobBounds } from '@components/Checkout/steps/CustomerInfoStep/utils/customerInfoStepUtils';

const buildPasswordSchema = (req?: PasswordRequirements) => {
  let schema = yup.string().required('A password is required');
  if (!req) return schema;
  schema = schema.min(req.minLength, `At least ${req.minLength} characters`);
  if (req.maxLength > 0) schema = schema.max(req.maxLength, `At most ${req.maxLength} characters`);
  if (req.uppercase) schema = schema.matches(/[A-Z]/, 'Must contain an uppercase letter');
  if (req.lowercase) schema = schema.matches(/[a-z]/, 'Must contain a lowercase letter');
  if (req.digit) schema = schema.matches(/\d/, 'Must contain a number');
  if (req.special) schema = schema.matches(/[!@#$%^&*(),.?":{}|<>]/, 'Must contain a special character');
  return schema;
};

export const buildCustomerInfoSchema = (req?: PasswordRequirements, tmfEnabled = false) => {
  const { today, minDateValue, minBirthDate } = getDobBounds();

  return yup.object({
    titleId: yup.string(),
    firstName: yup.string().required('A first name is required'),
    middleName: yup.string(),
    lastName: yup.string().required('A last name is required'),
    email: yup.string().email('Invalid email').required('A email is required'),
    confirmEmail: yup
      .string()
      .oneOf([yup.ref('email')], 'Emails must match')
      .required('Confirming the email is required'),
    dateOfBirth: yup
      .date()
      .nullable()
      .transform((value, original) => (original === '' ? null : value))
      .min(minDateValue, 'Date of birth must be after January 1, 1900')
      .max(today, 'Date of birth cannot be in the future')
      .test('min-age', 'You must be at least 18 years old', (value) => {
        if (!value) return true;
        return value <= minBirthDate;
      })
      .required('A date of birth is required'),
    howDidYouHearAboutUsOther: yup.string().when('howDidYouHearAboutUs', {
      is: 'Other',
      then: (s) => s.required('Please specify how you heard about us'),
      otherwise: (s) => s.notRequired(),
    }),
    primaryPhoneNumber: yup
      .string()
      .required('A primary contact number is required')
      .test('phone', 'Please enter a valid phone number', isPhoneNumberValid),
    primaryPhoneNumberType: yup.string().required('Select an option is required '),
    password: tmfEnabled ? yup.string().notRequired() : buildPasswordSchema(req),
    confirmPassword: tmfEnabled
      ? yup.string().notRequired()
      : yup
          .string()
          .oneOf([yup.ref('password')], 'Confirming the password is required')
          .required('Confirm password is required'),
    billingStreet: yup.string().when('useSameAddress', {
      is: false,
      then: (s) => s.required('An address is required'),
    }),
    billingUnit: yup.string(),
    billingCity: yup.string().when('useSameAddress', {
      is: false,
      then: (s) => s.required('A city is required'),
    }),
    billingProvince: yup.string().when('useSameAddress', {
      is: false,
      then: (s) => s.required('A province/state is required'),
    }),
    billingPostalCode: yup.string().when('useSameAddress', {
      is: false,
      then: (s) => s.required('A postal/zip code is required'),
    }),
    billingCountry: yup.string().when('useSameAddress', {
      is: false,
      then: (s) => s.required('A country is required'),
    }),
  });
};
